import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, addDays, isToday, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell,
  PieChart, Pie, LineChart, Line, AreaChart, Area, Tooltip,
  ComposedChart
} from 'recharts';
import { calendarAPI, userAPI, teacherAPI, practiceAPI, notificationAPI } from '../../services';
import { UnifiedScheduleEvent } from '../../services/calendarAPI';
import { DEFAULT_TEACHER_ID } from '../../config/constants';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useIsMobile } from '../../hooks/useIsMobile';
import AnnouncementModal from '../../components/notification/AnnouncementModal';
import { AnnouncementData } from '../../types/notification';

// SVG 아이콘
const Icon = ({ name, className, size = 16 }: { name: string; className?: string; size?: number }) => {
  const icons: Record<string, React.ReactNode> = {
    book: <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />,
    clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    fileText: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
    refresh: <><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
    x: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
    music: <><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>,
    message: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
    clipboard: <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /></>,
    target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
    bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
    chevronRight: <polyline points="9 18 15 12 9 6" />,
    arrowRight: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    trendUp: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>,
    trendDown: <><polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" /></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
    star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
    activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
    send: <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>,
    calendarOff: <><path d="M4.18 4.18A2 2 0 0 0 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 1.82-1.18" /><path d="M21 15.5V6a2 2 0 0 0-2-2H9.5" /><line x1="16" y1="2" x2="16" y2="4" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="2" y1="2" x2="22" y2="22" /></>
  };

  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

interface WeeklySchedule {
  date: string;
  dayName: string;
  shortDay: string;
  schedules: UnifiedScheduleEvent[];
  count: number;
  isToday: boolean;
}

interface DayEvent {
  date: string;
  schedules?: UnifiedScheduleEvent[];
}

interface PracticeSession {
  startTime?: string;
  status?: string;
  userName?: string;
  roomName?: string;
}

interface PracticeData {
  date: string;
  dayName: string;
  count: number;
  sessions: PracticeSession[];
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      navigate('/admin/lesson-schedule', { replace: true });
    }
  }, [isMobile, navigate]);

  useScrollToTop();

  const [stats, setStats] = useState({
    totalStudents: 0,
    pendingLessons: 0,
    pendingOneDay: 0,
    pendingModifications: 0,
    pendingCancellations: 0,
    todayLessons: 0,
    todayPractice: 0
  });

  const [weeklySchedules, setWeeklySchedules] = useState<WeeklySchedule[]>([]);
  const [todaySchedules, setTodaySchedules] = useState<UnifiedScheduleEvent[]>([]);
  const [practiceData, setPracticeData] = useState<PracticeData[]>([]);
  const [loading, setLoading] = useState(true);

  // 모달 상태
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [isSendingAnnouncement, setIsSendingAnnouncement] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadWeeklySchedules(),
        loadPracticeData()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        userAPI.getUsers({ status: 'active', limit: 1 }),
        teacherAPI.getPendingCounts()
      ]);

      const today = format(new Date(), 'yyyy-MM-dd');
      const todayRes = await calendarAPI.getUnifiedCalendar({
        startDate: today,
        endDate: today,
        teacherId: DEFAULT_TEACHER_ID,
        includeRegular: true
      });

      let todayCount = 0;
      if (todayRes.data?.success) {
        const events = (todayRes.data.data.events || []) as DayEvent[];
        const todayEvent = events.find((e) => e.date === today);
        if (todayEvent?.schedules) {
          const filtered = todayEvent.schedules.filter(
            (s) => s.scheduleType !== 'cancelled'
          );
          todayCount = filtered.length;
          setTodaySchedules(filtered.sort((a, b) =>
            a.startTime.localeCompare(b.startTime)
          ));
        }
      }

      setStats(prev => ({
        ...prev,
        totalStudents: usersRes.data?.pagination?.total || 0,
        pendingLessons: pendingRes.data?.lessonRequests || 0,
        pendingOneDay: pendingRes.data?.oneDayRequests || 0,
        pendingModifications: pendingRes.data?.modifications || 0,
        pendingCancellations: pendingRes.data?.cancellations || 0,
        todayLessons: todayCount
      }));
    } catch {
      // 통계 로드 실패
    }
  };

  const loadWeeklySchedules = async () => {
    try {
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);

      const response = await calendarAPI.getUnifiedCalendar({
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd'),
        teacherId: DEFAULT_TEACHER_ID,
        includeRegular: true,
        includeModifications: true
      });

      if (response.data?.success) {
        const events = (response.data.data.events || []) as DayEvent[];
        const weekData: WeeklySchedule[] = [];

        for (let i = 0; i < 7; i++) {
          const date = addDays(weekStart, i);
          const dateStr = format(date, 'yyyy-MM-dd');
          const dayEvent = events.find((e) => e.date === dateStr);
          const schedules = dayEvent?.schedules?.filter(
            (s) => s.scheduleType !== 'cancelled'
          ) || [];

          weekData.push({
            date: dateStr,
            dayName: format(date, 'EEEE', { locale: ko }),
            shortDay: format(date, 'E', { locale: ko }),
            schedules: schedules.sort((a: UnifiedScheduleEvent, b: UnifiedScheduleEvent) =>
              a.startTime.localeCompare(b.startTime)
            ),
            count: schedules.length,
            isToday: isToday(date)
          });
        }

        setWeeklySchedules(weekData);
      }
    } catch {
      // 주간 일정 로드 실패
    }
  };

  const loadPracticeData = async () => {
    try {
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);

      const response = await practiceAPI.getAllPracticeSessions({
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd'),
        limit: 100
      });

      if (response.success && response.data) {
        const responseData = response.data as { sessions?: PracticeSession[] };
        const sessions = responseData.sessions || [];
        const chartData: PracticeData[] = [];

        for (let i = 0; i < 7; i++) {
          const date = addDays(weekStart, i);
          const dateStr = format(date, 'yyyy-MM-dd');

          const daySessions = sessions.filter((s) => {
            const sessionDate = s.startTime ? format(new Date(s.startTime), 'yyyy-MM-dd') : null;
            return sessionDate === dateStr && s.status !== 'cancelled';
          });

          chartData.push({
            date: dateStr,
            dayName: format(date, 'E', { locale: ko }),
            count: daySessions.length,
            sessions: daySessions
          });

          if (isToday(date)) {
            setStats(prev => ({ ...prev, todayPractice: daySessions.length }));
          }
        }

        setPracticeData(chartData);
      }
    } catch {
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const emptyData: PracticeData[] = [];
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStart, i);
        emptyData.push({
          date: format(date, 'yyyy-MM-dd'),
          dayName: format(date, 'E', { locale: ko }),
          count: 0,
          sessions: []
        });
      }
      setPracticeData(emptyData);
    }
  };

  const totalPending = useMemo(() =>
    stats.pendingLessons + stats.pendingOneDay + stats.pendingModifications + stats.pendingCancellations,
    [stats]
  );

  const weeklyTotal = useMemo(() =>
    weeklySchedules.reduce((sum, day) => sum + day.count, 0),
    [weeklySchedules]
  );

  const practiceTotal = useMemo(() =>
    practiceData.reduce((sum, day) => sum + day.count, 0),
    [practiceData]
  );

  // 시간대별 수업 분포
  const hourlyDistribution = useMemo(() => {
    const hours: Record<number, number> = {};
    weeklySchedules.forEach(day => {
      day.schedules.forEach(s => {
        const hour = parseInt(s.startTime?.substring(0, 2) || '0');
        hours[hour] = (hours[hour] || 0) + 1;
      });
    });

    return Array.from({ length: 12 }, (_, i) => ({
      hour: `${i + 10}시`,
      count: hours[i + 10] || 0
    }));
  }, [weeklySchedules]);

  // 수업 유형별 통계
  const lessonTypeStats = useMemo(() => {
    const typeCount: Record<string, number> = {};
    weeklySchedules.forEach(day => {
      day.schedules.forEach(s => {
        typeCount[s.scheduleType] = (typeCount[s.scheduleType] || 0) + 1;
      });
    });

    return [
      { name: '정규', value: typeCount['regular'] || 0, color: '#0D2453' },
      { name: '원데이', value: typeCount['oneday_class'] || 0, color: '#3B82F6' },
      { name: '변경', value: typeCount['rescheduled'] || 0, color: '#F59E0B' },
      { name: '개인', value: typeCount['personal'] || 0, color: '#64748B' },
    ].filter(item => item.value > 0);
  }, [weeklySchedules]);

  // 요일별 비교
  const dayOfWeekData = useMemo(() => {
    return weeklySchedules.map(day => ({
      day: day.shortDay,
      lessons: day.count,
      practice: practiceData.find(p => p.date === day.date)?.count || 0
    }));
  }, [weeklySchedules, practiceData]);

  // 최근 7일 추이 데이터 (실제 데이터 기반)
  const weeklyTrendData = useMemo(() => {
    return weeklySchedules.map(day => ({
      date: format(new Date(day.date), 'M/d'),
      수업: day.count,
      연습실: practiceData.find(p => p.date === day.date)?.count || 0
    }));
  }, [weeklySchedules, practiceData]);

  // 현재 수업중인 사람
  const currentLesson = useMemo(() => {
    const now = new Date();
    const currentTimeStr = format(now, 'HH:mm');

    return todaySchedules.find(schedule => {
      const start = schedule.startTime?.substring(0, 5) || '';
      const end = schedule.endTime?.substring(0, 5) || '';
      return currentTimeStr >= start && currentTimeStr <= end;
    });
  }, [todaySchedules]);

  const getScheduleTypeLabel = (type: string) => {
    switch (type) {
      case 'regular': return '정규';
      case 'oneday_class': return '원데이';
      case 'rescheduled': return '변경';
      case 'personal': return '개인';
      default: return type;
    }
  };

  // 미니 캘린더 데이터
  const miniCalendarDays = useMemo(() => {
    const today = new Date();
    const monthStart = startOfMonth(today);
    const monthEnd = endOfMonth(today);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const startDayOfWeek = getDay(monthStart);
    const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    return { days, paddingDays };
  }, []);

  // 대기 요청 테이블 데이터
  const pendingRequestsTable = useMemo(() => [
    { type: '수업 신청', count: stats.pendingLessons, status: stats.pendingLessons > 0 ? 'pending' : 'ok', path: '/admin/lesson-requests' },
    { type: '원데이 신청', count: stats.pendingOneDay, status: stats.pendingOneDay > 0 ? 'pending' : 'ok', path: '/admin/oneday-requests' },
    { type: '일정 변경', count: stats.pendingModifications, status: stats.pendingModifications > 0 ? 'warning' : 'ok', path: '/admin/lesson-modifications' },
    { type: '취소 요청', count: stats.pendingCancellations, status: stats.pendingCancellations > 0 ? 'danger' : 'ok', path: '/admin/lesson-cancellations' },
  ], [stats]);

  // 공지 발송
  const handleSendAnnouncement = async (data: AnnouncementData) => {
    setIsSendingAnnouncement(true);
    try {
      await notificationAPI.sendToAllUsers(data);
      alert('공지가 발송되었습니다.');
    } catch {
      alert('공지 발송에 실패했습니다.');
    } finally {
      setIsSendingAnnouncement(false);
    }
  };

  // 연습실 툴팁 커스텀
  const PracticeTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: PracticeData }> }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;

    // 시간순 정렬
    const sortedSessions = [...data.sessions].sort((a, b) => {
      const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
      const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
      return timeA - timeB;
    });

    return (
      <div style={{ background: '#1F2937', padding: '10px 12px', borderRadius: 4, fontSize: 12, color: '#fff' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{data.dayName} - {data.count}건</div>
        {sortedSessions.length > 0 ? (
          sortedSessions.slice(0, 5).map((s, i) => (
            <div key={i} style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>
              {s.startTime ? format(new Date(s.startTime), 'HH:mm') : ''} {s.userName || '이름없음'} {s.roomName ? `(${s.roomName})` : ''}
            </div>
          ))
        ) : (
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>예약 없음</div>
        )}
        {sortedSessions.length > 5 && (
          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4 }}>외 {sortedSessions.length - 5}건</div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="teacher-page">
        <div className="page-loading">
          <div className="loading-spinner" />
          <p>로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-full">
      {/* 좌측 사이드바 */}
      <aside className="dash-sidebar">
        <div className="sidebar-date">
          <span className="date-day">{format(new Date(), 'd')}</span>
          <span className="date-info">
            <span>{format(new Date(), 'EEEE', { locale: ko })}</span>
            <span>{format(new Date(), 'yyyy년 M월')}</span>
          </span>
        </div>

        {/* 현재 수업중 - 수업중일 때만 표시 */}
        {currentLesson && currentLesson.userId && (
          <div className="current-lesson-box" onClick={(e) => {
            e.preventDefault();
            navigate(`/admin/lesson-log/write?studentId=${currentLesson.userId}&date=${format(new Date(), 'yyyy-MM-dd')}`);
          }}>
            <div className="current-badge">수업중</div>
            <div className="current-info">
              <span className="current-time">{currentLesson.startTime?.substring(0, 5)} - {currentLesson.endTime?.substring(0, 5)}</span>
              <span className="current-name">{currentLesson.studentName || currentLesson.title || '수업'}</span>
              <span className="current-type">{getScheduleTypeLabel(currentLesson.scheduleType)}</span>
            </div>
            <div className="current-action">수업 기록 작성 →</div>
          </div>
        )}

        {/* 오늘 통계 */}
        <div className="today-stats">
          <div className="today-stat">
            <Icon name="book" className="stat-icon" />
            <div className="stat-content">
              <span className="stat-num">{stats.todayLessons}</span>
              <span className="stat-label">오늘 수업</span>
            </div>
          </div>
          <div className="today-stat">
            <Icon name="clock" className="stat-icon" />
            <div className="stat-content">
              <span className="stat-num">{stats.todayPractice}</span>
              <span className="stat-label">연습실</span>
            </div>
          </div>
        </div>

        {/* 미니 캘린더 */}
        <div className="mini-calendar">
          <div className="cal-header">
            <span>{format(new Date(), 'M월 yyyy', { locale: ko })}</span>
          </div>
          <div className="cal-weekdays">
            {['월', '화', '수', '목', '금', '토', '일'].map(d => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="cal-days">
            {Array.from({ length: miniCalendarDays.paddingDays }).map((_, i) => (
              <span key={`pad-${i}`} className="cal-day empty" />
            ))}
            {miniCalendarDays.days.map(day => (
              <span key={day.toISOString()} className={`cal-day ${isToday(day) ? 'today' : ''}`}>
                {format(day, 'd')}
              </span>
            ))}
          </div>
        </div>

        {/* 빠른 메뉴 */}
        <div className="quick-actions">
          <button onClick={() => navigate('/admin/lesson-schedule')}>
            <Icon name="calendar" /> 수업 일정
          </button>
          <button onClick={() => navigate('/admin/practice-booking')}>
            <Icon name="music" /> 연습실 관리
          </button>
          <button onClick={() => navigate('/admin/users')}>
            <Icon name="users" /> 회원 관리
          </button>
          <button onClick={() => navigate('/admin/contacts')}>
            <Icon name="message" /> 문의 확인
          </button>
        </div>
      </aside>

      {/* 메인 콘텐츠 */}
      <main className="dash-main">
        {/* 상단: 통계 카드 */}
        <div className="stats-row">
          <div className="stat-card" onClick={() => navigate('/admin/users')}>
            <Icon name="users" className="card-icon" />
            <div className="card-info">
              <span className="card-value">{stats.totalStudents}</span>
              <span className="card-label">전체 회원</span>
            </div>
          </div>
          <div className={`stat-card ${totalPending > 0 ? 'alert' : ''}`} onClick={() => navigate('/admin/lesson-requests')}>
            <Icon name="fileText" className="card-icon" />
            <div className="card-info">
              <span className="card-value">{totalPending}</span>
              <span className="card-label">대기 신청</span>
            </div>
            {totalPending > 0 && <span className="alert-dot" />}
          </div>
          <div className={`stat-card ${stats.pendingModifications > 0 ? 'alert' : ''}`} onClick={() => navigate('/admin/lesson-modifications')}>
            <Icon name="refresh" className="card-icon" />
            <div className="card-info">
              <span className="card-value">{stats.pendingModifications}</span>
              <span className="card-label">변경 요청</span>
            </div>
          </div>
          <div className="stat-card" onClick={() => navigate('/admin/lesson-cancellations')}>
            <Icon name="x" className="card-icon" />
            <div className="card-info">
              <span className="card-value">{stats.pendingCancellations}</span>
              <span className="card-label">취소 요청</span>
            </div>
          </div>
          <div className="stat-card">
            <Icon name="activity" className="card-icon" />
            <div className="card-info">
              <span className="card-value">{weeklyTotal}</span>
              <span className="card-label">주간 수업</span>
            </div>
          </div>
        </div>

        {/* 차트 그리드 */}
        <div className="charts-grid expanded">
          {/* 오늘 수업 리스트 */}
          <div className="chart-card tall">
            <div className="card-header">
              <h3>오늘 수업</h3>
              <button className="view-all-btn" onClick={() => navigate('/admin/lesson-schedule')}>
                전체보기 <Icon name="arrowRight" />
              </button>
            </div>
            <div className="today-content">
              <div className="schedule-list-v">
                {todaySchedules.length === 0 ? (
                  <div className="empty-msg">오늘 예정된 수업이 없습니다</div>
                ) : (
                  todaySchedules.map((schedule, idx) => {
                    const isCurrent = currentLesson && schedule.startTime === currentLesson.startTime && schedule.studentName === currentLesson.studentName;
                    const isClickable = schedule.userId && ['regular', 'rescheduled', 'oneday_class'].includes(schedule.scheduleType);
                    return (
                      <div
                        key={idx}
                        className={`schedule-item type-${schedule.scheduleType} ${isCurrent ? 'current' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => {
                          if (isClickable) {
                            navigate(`/admin/lesson-log/write?studentId=${schedule.userId}&date=${format(new Date(), 'yyyy-MM-dd')}`);
                          }
                        }}
                      >
                        <span className="item-time">{schedule.startTime?.substring(0, 5)}</span>
                        <span className="item-name">{schedule.studentName || schedule.title || '수업'}</span>
                        {isCurrent ? (
                          <span className="item-badge live">수업중</span>
                        ) : (
                          <span className="item-badge">{getScheduleTypeLabel(schedule.scheduleType)}</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* 수업 유형 파이 차트 */}
              <div className="type-section">
                <div className="type-header">이번 주 수업 유형</div>
                <div className="type-chart-row">
                  {lessonTypeStats.length > 0 ? (
                    <>
                      <div className="pie-mini">
                        <ResponsiveContainer width={70} height={70}>
                          <PieChart>
                            <Pie data={lessonTypeStats} cx="50%" cy="50%" innerRadius={20} outerRadius={32} dataKey="value" stroke="none">
                              {lessonTypeStats.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="type-legend">
                        {lessonTypeStats.map((item, idx) => (
                          <div key={idx} className="legend-item">
                            <span className="dot" style={{ background: item.color }} />
                            <span className="name">{item.name}</span>
                            <span className="val">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="empty-msg">데이터 없음</div>
                  )}
                </div>
              </div>

              {/* 빠른 바로가기 */}
              <div className="quick-links">
                <button onClick={() => navigate('/admin/lesson-requests')}>
                  <Icon name="clipboard" />
                  수업 신청
                  {stats.pendingLessons > 0 && <em>{stats.pendingLessons}</em>}
                </button>
                <button onClick={() => navigate('/admin/oneday-requests')}>
                  <Icon name="target" />
                  원데이
                  {stats.pendingOneDay > 0 && <em>{stats.pendingOneDay}</em>}
                </button>
                <button onClick={() => setShowAnnouncementModal(true)}>
                  <Icon name="bell" />
                  공지
                </button>
                <button onClick={() => navigate('/admin/settings')}>
                  <Icon name="settings" />
                  설정
                </button>
              </div>
            </div>
          </div>

          {/* 주간 수업 (Bar) */}
          <div className="chart-card">
            <div className="card-header">
              <h3>이번 주 수업</h3>
              <span className="header-sub">총 {weeklyTotal}건</span>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklySchedules} barCategoryGap="25%">
                  <XAxis dataKey="shortDay" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis hide domain={[0, 'dataMax + 2']} />
                  <Tooltip contentStyle={{ background: '#1F2937', border: 'none', borderRadius: 4, fontSize: 12, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} formatter={(value) => [`${value}건`, '수업']} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {weeklySchedules.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.isToday ? '#0D2453' : '#CBD5E1'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 연습실 예약 (Line) - 커스텀 툴팁 */}
          <div className="chart-card">
            <div className="card-header">
              <h3>연습실 예약</h3>
              <span className="header-sub">총 {practiceTotal}건</span>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={practiceData}>
                  <XAxis dataKey="dayName" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis hide domain={[0, 'dataMax + 1']} />
                  <Tooltip content={<PracticeTooltip />} />
                  <Line type="linear" dataKey="count" stroke="#0D2453" strokeWidth={2} dot={{ r: 4, fill: '#0D2453', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 시간대별 (Area) */}
          <div className="chart-card wide">
            <div className="card-header">
              <h3>시간대별 수업 분포</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={hourlyDistribution}>
                  <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748B' }} interval={1} />
                  <YAxis hide domain={[0, 'dataMax + 1']} />
                  <Tooltip contentStyle={{ background: '#1F2937', border: 'none', borderRadius: 4, fontSize: 12, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} formatter={(value) => [`${value}건`, '수업']} />
                  <Area type="monotone" dataKey="count" stroke="#0D2453" fill="rgba(13, 36, 83, 0.12)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 빠른 관리 */}
          <div className="chart-card">
            <div className="card-header">
              <h3>빠른 관리</h3>
            </div>
            <div className="action-buttons">
              <button className="action-btn" onClick={() => navigate('/admin/lesson-schedule', { state: { openModal: true } })}>
                <Icon name="plus" />
                <span>개인일정 등록</span>
              </button>
              <button className="action-btn" onClick={() => navigate('/admin/lesson-schedule', { state: { openModal: true, isHoliday: true } })}>
                <Icon name="calendarOff" />
                <span>휴무일 설정</span>
              </button>
              <button className="action-btn" onClick={() => setShowAnnouncementModal(true)}>
                <Icon name="send" />
                <span>전체공지 발송</span>
              </button>
              <button className="action-btn" onClick={() => navigate('/admin/practice-booking')}>
                <Icon name="music" />
                <span>연습실 관리</span>
              </button>
            </div>
          </div>

          {/* 대기 현황 테이블 */}
          <div className="chart-card">
            <div className="card-header">
              <h3>대기 현황</h3>
            </div>
            <div className="table-body">
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th>건수</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRequestsTable.map((row, idx) => (
                    <tr key={idx} onClick={() => navigate(row.path)}>
                      <td>{row.type}</td>
                      <td className="num">{row.count}</td>
                      <td>
                        <span className={`status-dot ${row.status}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 요일별 비교 (Composed) */}
          <div className="chart-card">
            <div className="card-header">
              <h3>요일별 현황</h3>
              <div className="legend-inline">
                <span><i style={{ background: '#0D2453' }} />수업</span>
                <span><i style={{ background: '#94A3B8' }} />연습실</span>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dayOfWeekData}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis hide />
                  <Tooltip contentStyle={{ background: '#1F2937', border: 'none', borderRadius: 4, fontSize: 12, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                  <Bar dataKey="lessons" fill="#0D2453" radius={[2, 2, 0, 0]} barSize={12} name="수업" />
                  <Line type="monotone" dataKey="practice" stroke="#94A3B8" strokeWidth={2} dot={{ r: 3, fill: '#94A3B8' }} name="연습실" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 7일 추이 */}
          <div className="chart-card">
            <div className="card-header">
              <h3>7일 추이</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyTrendData}>
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748B' }} />
                  <YAxis hide />
                  <Tooltip contentStyle={{ background: '#1F2937', border: 'none', borderRadius: 4, fontSize: 12, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                  <Line type="monotone" dataKey="수업" stroke="#0D2453" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="연습실" stroke="#94A3B8" strokeWidth={2} dot={false} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 빠른 통계 */}
          <div className="chart-card">
            <div className="card-header">
              <h3>빠른 통계</h3>
            </div>
            <div className="quick-stats-list">
              <div className="qs-item">
                <span className="qs-label">오늘 수업</span>
                <span className="qs-value">{stats.todayLessons}건</span>
              </div>
              <div className="qs-item">
                <span className="qs-label">오늘 연습실</span>
                <span className="qs-value">{stats.todayPractice}건</span>
              </div>
              <div className="qs-item">
                <span className="qs-label">주간 수업</span>
                <span className="qs-value">{weeklyTotal}건</span>
              </div>
              <div className="qs-item">
                <span className="qs-label">주간 연습실</span>
                <span className="qs-value">{practiceTotal}건</span>
              </div>
            </div>
          </div>

          {/* 수업 유형 비율 */}
          <div className="chart-card">
            <div className="card-header">
              <h3>수업 유형 비율</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={lessonTypeStats.length > 0 ? lessonTypeStats : [{ name: '데이터 없음', value: 1, color: '#E5E7EB' }]}
                    cx="50%"
                    cy="50%"
                    innerRadius="40%"
                    outerRadius="70%"
                    dataKey="value"
                    stroke="none"
                    label={({ name, percent }) => lessonTypeStats.length > 0 && percent ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={false}
                  >
                    {(lessonTypeStats.length > 0 ? lessonTypeStats : [{ color: '#E5E7EB' }]).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1F2937', border: 'none', borderRadius: 4, fontSize: 12, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </main>

      {/* 전체공지 모달 */}
      <AnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        onSend={handleSendAnnouncement}
        isSending={isSendingAnnouncement}
      />
    </div>
  );
};

export default Dashboard;
